import React, { useCallback, useMemo, useState } from "react"
import { Button } from "~/ui-rtk/components/ui/controls"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"

import type { ConnectorStatus } from "~/ui-rtk/shared/types/connectors"
import { CredentialGrantWarningChip } from "~/ui-rtk/components/features/connectors/CredentialGrantWarning"
import {
  getConnectorDescription,
  ConnectorMetadataExtended,
} from "../../utils/connectorDescriptions"
import { serviceConnectorMap } from "~/ui-rtk/components/features/connectors/forms/utils"
import { useConnectorsTracking } from "~/ui-rtk/hooks/useConnectorsTracking"
import {
  useCompanyConnectorControllerGetAirbyteSetupErrorQuery,
  useCompanyConnectorControllerGetCompanyConnectorAccountInfoQuery,
} from "~/ui-rtk/api/companyConnectorApi"
import {
  SetupErrorBanner,
  type AirbyteSetupErrorData,
  ERROR_ELIGIBLE_STATUSES,
} from "~/ui-rtk/components/features/connectors/SetupErrorBanner"
import ResetSetupConfirmDialog from "~/ui-rtk/components/features/connectors/ResetSetupConfirmDialog"
import {
  isMetaDataFallbackConnector,
  isMetaSystemUserConnector,
  RESETTABLE_STATUSES,
} from "~/ui-rtk/utils/connector-utils"

type ServiceConnectorConfig = {
  type: "connector" | "upload"
  component: React.ComponentType<Record<string, unknown>>
  showForStatuses?: string[]
}

type TSourceItemProps = {
  id: string
  /**
   * The CompanyConnector UUID for this card's primary connection, when one
   * exists. Used by endpoints that operate on a specific CC (setup-error
   * fetch, reset). For services with multiple CCs, hide single-target
   * actions and direct the user to the per-CC list inside the dialog.
   */
  companyConnectorId?: string
  /**
   * True when this service has more than one CompanyConnector for the
   * current company. Hides the main-card "Start over" so users go through
   * the per-CC reset buttons in ConnectionList instead.
   */
  hasMultipleConnectors?: boolean
  name: string
  serviceName?: string
  image: {
    alt: string
    src: string
  }
  status: ConnectorStatus
  metadata?: ConnectorMetadataExtended
  isBeta?: boolean
  isAirbyte?: boolean
  isCustom?: boolean
  confirmedAt?: string
  hasVersionMismatch?: boolean
  integrationsReady?: boolean
  onConnect: (id: string, callback: () => void) => void
  onStatusCheck?: (id: string) => void
}

export const getButtonText = (
  status: ConnectorStatus,
  serviceName?: string,
): string => {
  // Check if this service has custom form configuration for setup statuses
  const connectorConfig: ServiceConnectorConfig | undefined = serviceName
    ? (serviceConnectorMap[serviceName] as ServiceConnectorConfig | undefined)
    : undefined
  const hasSetupFlow =
    connectorConfig?.showForStatuses &&
    connectorConfig.showForStatuses.length > 0

  // If service has setup flow and status is in setup statuses, use setup-focused text
  if (hasSetupFlow && connectorConfig?.showForStatuses?.includes(status)) {
    switch (status) {
      case "PENDING_SETUP":
        return "Finish setup"
      case "FAILED":
        return "Complete setup"
      case "RECONNECT":
        return "Complete setup"
      default:
        return "Continue setup"
    }
  }

  // Default button text for other services or non-setup statuses
  const buttonTextMap: { [key in ConnectorStatus]: string } = {
    PENDING_SETUP: "Finish setup",
    CONNECTED: "Connected",
    FAILED: "Try again",
    NOT_CONNECTED: "Connect",
    RECONNECT: "Reconnect",
    UPGRADE: "Upgrade",
    PENDING: "Downloading",
  }

  return buttonTextMap[status]
}

const SourceItem: React.FC<TSourceItemProps> = ({
  id,
  companyConnectorId,
  hasMultipleConnectors,
  name,
  serviceName,
  image,
  status,
  metadata,
  confirmedAt,
  isBeta,
  isAirbyte,
  isCustom,
  hasVersionMismatch,
  integrationsReady = true,
  onConnect,
  onStatusCheck,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const {
    trackConnectClicked,
    trackReconnectClicked,
    trackShowDetailsClicked,
  } = useConnectorsTracking()

  // Surface backend-persisted Airbyte setup errors inline so customers can see
  // what went wrong without opening the detail dialog. The endpoint returns
  // null for statuses that don't have a stored error, so we only hit it when
  // the connector is actually stuck in a setup-error-eligible status. Skip
  // when there's no resolved CC id (the card is in a NOT_CONNECTED state or
  // the CC list hasn't loaded yet) since the endpoint is keyed on CC UUID.
  const shouldFetchSetupError =
    !!companyConnectorId && ERROR_ELIGIBLE_STATUSES.includes(status)
  const { data: setupErrorRaw } =
    useCompanyConnectorControllerGetAirbyteSetupErrorQuery(
      { id: companyConnectorId ?? "" },
      { skip: !shouldFetchSetupError },
    )
  // The generated type is `{ message: string } | null`, but the backend also
  // writes `timestamp` and `isValidationError`. Cast to the runtime shape.
  const setupError = setupErrorRaw as AirbyteSetupErrorData | null | undefined

  // For services where the card description needs the live-resolved account
  // (e.g. instagram_insights showing the @username of the picked igUserId)
  // pull the same account-info the connector info modal uses. Skip unless
  // we have a CC id, the connector is connected, AND only one connection
  // exists — when there are multiple CCs for this service the card can't
  // pick one to spotlight, so it just shows "Connected".
  const shouldFetchAccountInfo =
    !!companyConnectorId &&
    status === "CONNECTED" &&
    !hasMultipleConnectors &&
    serviceName === "instagram_insights"
  const { data: accountInfo, isFetching: isAccountInfoFetching } =
    useCompanyConnectorControllerGetCompanyConnectorAccountInfoQuery(
      { id: companyConnectorId ?? "" },
      { skip: !shouldFetchAccountInfo },
    )

  // Check if connector was created by migration
  const isCreatedByMigration = metadata?.createdByMigration === true

  // A Meta system user on the row: it has no per-person grant (see below).
  const isMetaSystemUser = isMetaSystemUserConnector(metadata)
  // APP-548 phase 4b: a system user standing in for a failed login. The brand
  // still needs to reconnect, and its login takes the row back.
  const isDataFallback = isMetaDataFallbackConnector(metadata)

  // A CONNECTED connector whose Meta grant no longer covers its account, or
  // whose data runs on the system-user fallback, is only right again after a
  // reconnect, so the primary button becomes one (the same flow the
  // RECONNECT status uses). Other statuses already offer
  // a connect/retry action; the chip alone is enough there. A system user
  // has no per-person grant, so a warning left on its row is stale.
  const grantWarning = isMetaSystemUser
    ? null
    : (metadata?.credentialGrantWarning ?? null)
  const grantNeedsReconnect =
    (!!grantWarning || isDataFallback) && status === "CONNECTED"
  const buttonText = grantNeedsReconnect
    ? "Reconnect"
    : getButtonText(status, serviceName)

  // Calculate days until Nov 30, 2025
  const daysUntilExpiration = useMemo(() => {
    const targetDate = new Date("2025-12-06T23:59:59")
    const today = new Date()
    const diffTime = targetDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }, [])

  const handleConnect = useCallback(() => {
    if (!integrationsReady || isCreatedByMigration) {
      return
    }
    const isReconnect =
      ["RECONNECT", "FAILED", "UPGRADE"].includes(status) || grantNeedsReconnect
    const eventData = {
      connector_id: id,
      connector_name: name,
      service_name: serviceName,
      connector_status: status,
      is_airbyte: isAirbyte,
      is_custom: isCustom,
      is_beta: isBeta,
      has_version_mismatch: hasVersionMismatch,
      is_created_by_migration: isCreatedByMigration,
      button_text: buttonText,
    }
    if (isReconnect) {
      trackReconnectClicked(eventData)
    } else {
      trackConnectClicked(eventData)
    }
    void setIsSubmitting(true)
    void onConnect(id, () => setIsSubmitting(false))
  }, [
    id,
    name,
    serviceName,
    status,
    isAirbyte,
    isCustom,
    isBeta,
    hasVersionMismatch,
    isCreatedByMigration,
    grantNeedsReconnect,
    buttonText,
    onConnect,
    integrationsReady,
    trackConnectClicked,
    trackReconnectClicked,
  ])

  const handleStatusCheck = useCallback(() => {
    trackShowDetailsClicked({
      connector_id: id,
      connector_name: name,
      service_name: serviceName,
      connector_status: status,
      is_airbyte: isAirbyte,
      is_custom: isCustom,
    })
    void onStatusCheck?.(id)
  }, [
    id,
    name,
    serviceName,
    status,
    isAirbyte,
    isCustom,
    onStatusCheck,
    trackShowDetailsClicked,
  ])

  const isDisabled =
    (!integrationsReady && status === "NOT_CONNECTED") || isCreatedByMigration

  return (
    <li
      className={`flex justify-between w-full p-[12px] rounded-xl relative overflow-visible ${
        isDisabled || isCreatedByMigration ? "opacity-60" : ""
      } ${
        isAirbyte
          ? "border-2 border-brand/30 bg-[#222446] shadow-md hover:border-brand/50 transition-all duration-300"
          : "border border-basic-blue bg-[#222446]"
      }`}
    >
      {/* Airbyte subtle gradient background */}
      {isAirbyte && (
        <div className="absolute inset-0 bg-gradient-to-br from-brand/5 via-transparent to-brand/5 pointer-events-none" />
      )}

      <div className="flex items-start gap-system-sm relative z-10">
        <div className="relative">
          <img
            src={image.src}
            alt={image.alt}
            className={`h-system-2xl max-w-system-2xl ${
              isDisabled ? "grayscale" : ""
            }`}
          />
          {isBeta && (
            <div className="flex items-center bg-brand rounded-md px-2 py-0.5 absolute -top-2 -right-2 shadow-lg">
              <span className="text-[10px] text-white leading-none font-bold tracking-wide">
                BETA
              </span>
            </div>
          )}
        </div>
        <div className="text-left flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium leading-5 text-white">{name}</p>

            {/* Upgraded badge for Airbyte connectors */}
            {isAirbyte && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-system-success-700 to-system-success-500 shadow-md">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shadow-sm" />
                <span className="text-[10px] text-white leading-none font-bold tracking-wide">
                  Upgraded
                </span>
              </div>
            )}

            {/* Legacy badge for Fivetran connectors only (not Airbyte, not Custom) */}
            {!isAirbyte && !isCustom && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-system-yellow-600 to-system-orange-600 shadow-md">
                <ExclamationTriangleIcon className="w-3 h-3 text-white" />
                <div className="flex flex-col">
                  <span className="text-[10px] text-white leading-none font-bold tracking-wide">
                    Legacy
                  </span>
                  <span className="text-[8px] text-white/90 leading-tight mt-0.5">
                    Expires in {daysUntilExpiration} days
                  </span>
                </div>
              </div>
            )}

            {hasVersionMismatch && (
              <div className="flex items-center gap-1 px-2 py-1 bg-system-orange-600 rounded-full">
                <ExclamationTriangleIcon className="w-3 h-3 text-white" />
                <span className="text-xs font-medium text-white whitespace-nowrap">
                  Update Available
                </span>
              </div>
            )}

            {isDataFallback && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-600 rounded-full">
                <span className="text-[10px] text-white leading-none font-bold tracking-wide">
                  Data: system user (fallback)
                </span>
              </div>
            )}

            {/* System Migration badge */}
            {isCreatedByMigration && (
              <div className="flex items-center gap-1 px-2 py-1 bg-system-gray-600 rounded-full">
                <ExclamationTriangleIcon className="w-3 h-3 text-white" />
                <span className="text-[10px] text-white leading-none font-bold tracking-wide">
                  System Managed
                </span>
              </div>
            )}

            {/* Meta grant narrowed by another company's reconnect (API metadata.credentialGrantWarning) */}
            {grantWarning && (
              <CredentialGrantWarningChip warning={grantWarning} />
            )}
          </div>
          <div className="flex items-center gap-1 mt-1">
            <p className="text-sm text-gray-300">
              {getConnectorDescription(
                serviceName || id,
                status,
                metadata,
                confirmedAt,
                name, // connector name for version warnings
                undefined,
                undefined,
                accountInfo,
                shouldFetchAccountInfo && isAccountInfoFetching,
                hasMultipleConnectors,
              )}
            </p>
            {status !== "NOT_CONNECTED" && (
              <>
                <span className="text-gray-500 text-sm">•</span>
                <button
                  onClick={handleStatusCheck}
                  className="text-blue-400 hover:text-blue-300 text-sm underline cursor-pointer transition-colors"
                >
                  Show details
                </button>
              </>
            )}
            {RESETTABLE_STATUSES.includes(status) &&
              !isCreatedByMigration &&
              !isDisabled &&
              !!companyConnectorId &&
              !hasMultipleConnectors && (
                <>
                  <span className="text-gray-500 text-sm">•</span>
                  <button
                    type="button"
                    onClick={() => setIsResetDialogOpen(true)}
                    className="text-gray-400 hover:text-gray-200 text-sm underline cursor-pointer transition-colors"
                    title="Delete current setup progress and reconnect from scratch"
                  >
                    Start over
                  </button>
                </>
              )}
          </div>
          {shouldFetchSetupError && setupError?.message && (
            <SetupErrorBanner
              error={setupError}
              variant="dense"
              className="mt-2"
            />
          )}
        </div>
      </div>

      <div className="flex md:items-center sm:items-center gap-1 relative z-10">
        <Button
          className={`w-[82px] h-[40px] col-span-5 cursor-pointer p-0 ${
            isAirbyte && status === "NOT_CONNECTED"
              ? "shadow-md shadow-brand/20"
              : ""
          }`}
          disabled={
            isDisabled ||
            isSubmitting ||
            status === "PENDING" ||
            (status === "CONNECTED" && !grantNeedsReconnect) ||
            isCreatedByMigration
          }
          variant={{
            variant: "solid",
            color:
              ["FAILED", "RECONNECT", "UPGRADE"].includes(status) ||
              grantNeedsReconnect
                ? "red"
                : "pink",
          }}
          // isLoading={isSubmitting || status === "PENDING"}
          onClick={handleConnect}
          title={(() => {
            if (isCreatedByMigration) {
              return "This connector was created by the system migration and cannot be re-authenticated. Please contact your administrator if you need to modify this connection."
            }
            if (isDisabled) {
              return "We're doing cool stuff, just wait a couple of seconds before connectors are ready..."
            }
            return undefined
          })()}
        >
          <p className="text-white text-[12px] font-bold leading-[145%]">
            {buttonText}
          </p>
        </Button>
      </div>
      {companyConnectorId && (
        <ResetSetupConfirmDialog
          isOpen={isResetDialogOpen}
          companyConnectorId={companyConnectorId}
          connectorName={name}
          onClose={() => setIsResetDialogOpen(false)}
        />
      )}
    </li>
  )
}

export default SourceItem
